import { useState, useEffect } from 'react';
import { useCalculatorStore } from '@/store/calculator';
import { AlertTriangle, CheckCircle, Gauge, RefreshCw, Info } from 'lucide-react';
import { cn } from '@/lib/utils';
import * as Popover from '@radix-ui/react-popover';

export function DebtServiceRatioSlider() {
  const { settings, updateSetting } = useCalculatorStore();
  const [localValue, setLocalValue] = useState(Math.round(settings.debtServiceRatio * 100));
  
  // Keep local state in sync with store
  useEffect(() => {
    setLocalValue(Math.round(settings.debtServiceRatio * 100));
  }, [settings.debtServiceRatio]);
  
  // Update store when slider changes
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = parseInt(e.target.value, 10);
    setLocalValue(newValue);
  };
  
  // Only update store when slider interaction ends to prevent excessive calculations
  const handleChangeCommitted = () => {
    updateSetting('debtServiceRatio', localValue / 100);
  };

  // Reset to default 32%
  const handleReset = () => {
    const defaultValue = 32;
    setLocalValue(defaultValue);
    updateSetting('debtServiceRatio', defaultValue / 100);
  };
  
  // Determine text color and icon based on ratio value
  const getRiskIndicator = () => {
    if (localValue <= 28) {
      return {
        color: 'text-emerald-700',
        icon: CheckCircle,
        label: 'Conservative'
      };
    } else if (localValue <= 36) {
      return {
        color: 'text-primary', 
        icon: CheckCircle, 
        label: 'Standard'
      };
    } else {
      return {
        color: 'text-rose-800', 
        icon: AlertTriangle,
        label: 'Aggressive'
      };
    }
  };
  
  const riskIndicator = getRiskIndicator();
  const Icon = riskIndicator.icon;
  
  return (
    <div>
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Gauge className="w-5 h-5 text-primary" />
          <Popover.Root>
            <Popover.Trigger asChild>
              <button className="inline-flex items-center gap-1 hover:text-gray-900">
                <h4 className="text-sm font-medium text-gray-600">Debt Service Ratio</h4>
                <Info className="w-3.5 h-3.5 text-gray-400" />
              </button>
            </Popover.Trigger>
            <Popover.Portal>
              <Popover.Content
                className="z-50 w-72 p-3 bg-white rounded-lg shadow-lg border border-gray-200 text-sm text-gray-600"
                sideOffset={1}>
                The debt service ratio determines what percentage of your gross monthly income can go toward total housing expenses. This includes your mortgage payment (principal and interest), property taxes, home insurance, mortgage insurance (if down payment is less than 20%), and utilities. The default is 32%, which you can adjust using the slider. Traditional guidelines suggest keeping housing costs below 28% of income, with total debts below 36%. Some lenders now approve total debt ratios up to 45% for qualified borrowers, though higher ratios leave less income available for other expenses and savings.
                <Popover.Arrow className="fill-white" />
              </Popover.Content>
            </Popover.Portal>
          </Popover.Root>
          <button
            onClick={handleReset}
            className="p-1 rounded-full hover:bg-gray-100 active:bg-transparent focus:bg-transparent text-gray-500 touch-manipulation"
            title="Reset to 32%"
          >
            <RefreshCw className="w-3.5 h-3.5" />
          </button>
        </div>
        <div className={cn("flex items-center gap-1.5", riskIndicator.color)}>
          <Icon className="w-4 h-4" />
          <span className="text-sm font-medium">{riskIndicator.label}</span>
        </div>
      </div>
      
      <div className="flex items-center gap-3">
        <div className="flex-1">
          <input
            type="range"
            min="15"
            max="45"
            step="1"
            value={localValue}
            onChange={handleChange}
            onMouseUp={handleChangeCommitted}
            onTouchEnd={handleChangeCommitted}
            className="w-full appearance-none h-2 rounded-full"
            style={{
              background: `linear-gradient(to right, 
                #15803d 0%, 
                #15803d ${(28 - 15) * 100 / (45 - 15)}%, 
                #1A5173 ${(28 - 15) * 100 / (45 - 15)}%, 
                #1A5173 ${(36 - 15) * 100 / (45 - 15)}%, 
                #9f1239 ${(36 - 15) * 100 / (45 - 15)}%, 
                #9f1239 100%)`,
              WebkitAppearance: 'none',
              outline: 'none',
              cursor: 'pointer'
            }}
          />
          
          {/* Custom styles for the slider thumb */}
          <style>{`
            input[type=range]::-webkit-slider-thumb {
              -webkit-appearance: none;
              appearance: none;
              width: 20px;
              height: 20px;
              border-radius: 50%;
              background: white;
              border: 2px solid #1A5173;
              cursor: pointer;
              box-shadow: 0 2px 4px rgba(0,0,0,0.1);
            }
            
            input[type=range]::-moz-range-thumb {
              width: 20px;
              height: 20px;
              border-radius: 50%;
              background: white;
              border: 2px solid #1A5173;
              cursor: pointer;
              box-shadow: 0 2px 4px rgba(0,0,0,0.1);
            }
          `}</style>
        </div>
        
        <div className="w-12 text-center font-medium text-lg text-gray-900">
          {localValue}%
        </div>
      </div>
    </div>
  );
}
